import { cn } from '@/utils/cn';

/**
 * One milestone on the "How Wonder Works" curve: an oversized ghost numeral
 * behind a title and a line or two of copy.
 *
 * The numeral is taken out of the flow rather than laid out with the text.
 * In the design it sits somewhere different on every step -- beside the copy
 * on 1, above it on 2 and 3 -- and it overlaps the copy besides, so letting it
 * occupy space would drag each title off the dotted line dropping from its
 * node on the curve. Out of flow, `numeralClassName` moves it per step and the
 * text never shifts. It defaults to sitting just above the block, overlapping
 * slightly, which is where steps 2 and 3 put it.
 *
 * The text is wrapped in its own positioned element so it paints over the
 * numeral: an absolutely positioned child would otherwise sit above static
 * siblings no matter what the DOM order is, and the numeral would cover the
 * title instead of ghosting behind it.
 *
 * The numeral is offset with `top`/`bottom` and margin, never with a translate
 * utility. HowWonderWorks tweens its `y`, and GSAP folds any CSS `translate`
 * it finds into its own transform and resets the property -- so a translate
 * class here holds only until the animation runs, and holds for good under
 * reduced motion where it never does. Two layouts for one element, neither of
 * them the one that was drawn. Margin survives both.
 *
 * Renders an `<li>`, because the numeral is decoration and hidden from
 * assistive tech -- the ordering has to come from a wrapping `<ol>`. Override
 * `as` only if the sequence is conveyed some other way.
 */
export function JourneyStep({
  step,
  title,
  description,
  as: Component = 'li',
  numeralClassName,
  className,
  style,
}) {
  return (
    <Component className={cn('relative', className)} style={style}>
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute bottom-full left-0 -mb-5 select-none',
          // 9.75rem lands the digit at the 110px the frame draws it: Inter's
          // figures stand about 0.72 of the em, and 110 / 0.72 is 153.
          // Inter, not the section's Inter Tight: the frame's "2" is 92px wide
          // at this size, which is Inter's figure -- Tight's is about 84.
          'font-sans text-[6rem] leading-none font-bold text-white/15 lg:text-[9.75rem]',
          numeralClassName,
        )}
      >
        {step}
      </span>

      <div className="relative">
        <h3 className="text-on-night text-[1.375rem] leading-9 font-semibold">{title}</h3>
        {/*
          White at 70% rather than `text-on-night-muted`: the token is a lilac
          tint and the design calls for plain white knocked back, which reads
          cooler against the purple ground.
        */}
        {/*
          The measure is set by the two longer descriptions, which the frame
          breaks in two -- "Browse relevant internships / tailored to you." --
          and which a 10rem column broke in three.
        */}
        <p className="mt-3 max-w-58 text-lg leading-6 text-white/70">{description}</p>
      </div>
    </Component>
  );
}
